import apiClient, { safeJson } from '@/api/client';
import {
    SettlementClassificationFailure,
    getSettlementClassificationErrorCode,
    getSettlementClassificationErrorMessage,
} from './errors';
import { SettlementCategory } from './types';

/**
 * 상품 정산 분류를 이미 발급된 수강권에 일괄 적용 — BACKEND_HANDOFF_TEMPLATE_BULK.
 *
 * 웹은 계산하지 않는다. 개수·대상·수업료가 바뀌기 시작하는 달은 서버 값 그대로 쓴다.
 * 여기 있는 함수는 「서버 값을 어떤 문구·어떤 다음 동작으로 옮기는가」만 정한다.
 */

export interface TemplateCategoryBulkFeeImpact {
    exceptionRatesEffective: boolean;
    feeChangeStartMonth: { year: string; month: string } | null;
}

export interface TemplateCategoryBulkPendingJob {
    jobId: string;
    requestId: string;
    kind: 'APPLY' | 'REVERT';
    /** APPLY: 이어 하기 토큰(계약 §4-1). 미리보기 pendingJob 의 값만 믿는다 — PENDING_EXISTS details 의 값은 토큰이 아닐 수 있다. */
    previewHash: string | null;
    revertOfJobId: string | null;
    /** 그 토큰이 승인하는 「지금」 수업료 영향(APPLY). REVERT 는 null, 구서버는 없음. */
    feeImpact?: TemplateCategoryBulkFeeImpact | null;
}

export interface TemplateCategoryBulkPreview {
    templateId: string;
    templateName: string;
    toKey: string | null;
    previewHash: string;
    counts: {
        targets: { total: number; active: number; expired: number; deleted: number };
        alreadyToKey: number;
        protectedCorrected: number;
        protectedInherited: number;
        outOfScope: number;
        nullCenterIncluded: number;
    };
    fromKeys: { key: string | null; missing: boolean; count: number }[];
    exceptionRatesEffective: boolean;
    feeChangeStartMonth: { year: string; month: string } | null;
    maxTargets: number;
    pendingJob: TemplateCategoryBulkPendingJob | null;
}

export interface TemplateCategoryBulkJob {
    jobId: string;
    kind: 'APPLY' | 'REVERT';
    status: 'PENDING' | 'APPLIED' | string;
    templateId: string;
    toKey: string | null;
    revertOfJobId: string | null;
    revertedByJobId: string | null;
    counts: { planned: number; applied: number; skippedChanged: number };
    /** 적용 중 새로 생긴 수강권(인계·나눔 사본 등)을 다시 찾아 덧붙인 장수(counts 에 포함, 서버 10/18 F4). 구서버엔 없다. */
    sweptAdded?: number;
    /** 다시 찾기를 한도 때문에 다 못 끝냈을 때만 true(서버 G5). 남은 수강권은 옛 분류 그대로다. */
    sweepIncomplete?: boolean;
    /** sweepIncomplete 일 때 남은 장수(대략). */
    sweepRemaining?: number;
    replayed?: boolean;
    createdAt?: string | null;
    appliedAt?: string | null;
}

/* ------------------------------------------------------------------ API */

const bulkBase = (centerId: string, templateId: string) =>
    `v1/settlement-classification/center/${centerId}/templates/${encodeURIComponent(templateId)}/category-bulk`;


const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const isPendingJob = (value: unknown): value is TemplateCategoryBulkPendingJob => {
    if (!value || typeof value !== 'object') return false;
    const v = value as Record<string, unknown>;
    return typeof v.jobId === 'string' && typeof v.requestId === 'string' && (v.kind === 'APPLY' || v.kind === 'REVERT');
};

/**
 * 응답 모양이 계약과 다르면 null — 화면은 일괄 창을 띄우지 않는다(상품 저장만, 기존 동작).
 * 숫자 필드가 비어 있으면 0 으로 채우지만, 대상 총수와 hash 가 없으면 아예 믿지 않는다.
 */
export const parseTemplateCategoryBulkPreview = (raw: unknown): TemplateCategoryBulkPreview | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as any;
    const targets = r.counts?.targets;
    if (typeof r.previewHash !== 'string' || !r.previewHash) return null;
    if (!targets || typeof targets.total !== 'number' || !Number.isFinite(targets.total)) return null;
    const fee = r.feeChangeStartMonth;
    return {
        templateId: typeof r.templateId === 'string' ? r.templateId : '',
        templateName: typeof r.templateName === 'string' ? r.templateName : '',
        toKey: typeof r.toKey === 'string' ? r.toKey : null,
        previewHash: r.previewHash,
        counts: {
            targets: {
                total: targets.total,
                active: num(targets.active),
                expired: num(targets.expired),
                deleted: num(targets.deleted),
            },
            alreadyToKey: num(r.counts?.alreadyToKey),
            protectedCorrected: num(r.counts?.protectedCorrected),
            protectedInherited: num(r.counts?.protectedInherited),
            outOfScope: num(r.counts?.outOfScope),
            nullCenterIncluded: num(r.counts?.nullCenterIncluded),
        },
        fromKeys: Array.isArray(r.fromKeys) ? r.fromKeys : [],
        exceptionRatesEffective: r.exceptionRatesEffective === true,
        feeChangeStartMonth:
            fee && typeof fee === 'object' ? { year: String(fee.year ?? ''), month: String(fee.month ?? '') } : null,
        maxTargets: typeof r.maxTargets === 'number' && r.maxTargets > 0 ? r.maxTargets : 5000,
        pendingJob: isPendingJob(r.pendingJob) ? r.pendingJob : null,
    };
};

/** @method get @brief 이 상품으로 발급된 수강권 중 분류를 맞출 대상 미리보기 */
export const getTemplateCategoryBulkPreview = ({
    centerId,
    templateId,
    signal,
}: {
    centerId: string;
    templateId: string;
    signal?: AbortSignal;
}) =>
    // 본문이 멈춰도 30초 안에 끝난다(실패 → 「다시 확인」). 그래야 보이지 않는 흐름이 재진입을 막지 않는다.
    runWithDeadline(async (signal, deadline) => {
        // noAuthRetry: 401 이면 갱신·재전송 없이 실패 — 「다시 로그인」 안내로 끝난다.
        const res = await Promise.race([
            apiClient.get(`${bulkBase(centerId, templateId)}/preview`, kyOptions(signal, { noAuthRetry: true }) as any),
            deadline,
        ]);
        if (res === DEADLINE) throw timeoutError();
        const body = await readBody(res as Response, deadline);
        if (body === DEADLINE) throw timeoutError();
        return parseTemplateCategoryBulkPreview(body);
    }, signal);

/**
 * 성공(200) 응답 본문이 계약 모양인지. 본문이 잘리거나 비면 null — 서버는 이미 바꿨을 수 있으므로
 * 「0건」으로 읽지 않고 결과 미확인으로 다룬다(같은 requestId 로 다시 보내면 서버가 결과를 재생한다).
 */
export const parseTemplateCategoryBulkJob = (raw: unknown): TemplateCategoryBulkJob | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as any;
    if (typeof r.jobId !== 'string' || !r.jobId) return null;
    if (r.kind !== 'APPLY' && r.kind !== 'REVERT') return null;
    if (typeof r.status !== 'string') return null;
    if (!r.counts || typeof r.counts.applied !== 'number' || !Number.isFinite(r.counts.applied)) return null;
    return r as TemplateCategoryBulkJob;
};

/** 결과 미확인 오류 — 200 을 받았지만 본문을 읽지 못했다. */
export const TEMPLATE_CATEGORY_BULK_RESULT_UNCONFIRMED = 'TEMPLATE_CATEGORY_BULK_RESULT_UNCONFIRMED';

/**
 * 쓰기 요청 전체(헤더 + 본문)의 마감. ky 의 timeout 은 응답 헤더를 받으면 풀려서, 200 헤더 뒤 본문이 멈추면
 * 창이 「적용하는 중…」에 영원히 잠긴다. 마감이 지나면 요청을 끊고 결과 미확인으로 다룬다(같은 키 유지).
 */
export const TEMPLATE_CATEGORY_BULK_REQUEST_DEADLINE_MS = 30000;

const unconfirmedError = () =>
    Object.assign(new Error('일괄 작업 결과를 읽지 못했어요.'), {
        status: 200,
        resultUnconfirmed: true,
        code: TEMPLATE_CATEGORY_BULK_RESULT_UNCONFIRMED,
    });

const DEADLINE = Symbol('deadline');
type Deadline = typeof DEADLINE;

const timeoutError = () => Object.assign(new Error('응답이 늦어요.'), { name: 'TimeoutError' });

/**
 * 요청 전체(헤더 + 본문)를 마감 안에서 끝낸다. 마감이 지나면 연결을 끊고(AbortController) DEADLINE 을 돌려준다.
 * ky 의 timeout 은 헤더를 받으면 풀리므로 본문 정체는 이것으로만 막힌다.
 */
const runWithDeadline = async <T>(
    work: (signal: AbortSignal | undefined, deadline: Promise<Deadline>) => Promise<T>,
    /** 호출부(흐름)의 중단 신호 — 창 종료·세션 변경 때 진행 중 요청을 끊는다. */
    external?: AbortSignal,
): Promise<T> => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const onExternalAbort = () => controller?.abort();
    if (external) {
        if (external.aborted) controller?.abort();
        else external.addEventListener('abort', onExternalAbort);
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<Deadline>((resolve) => {
        timer = setTimeout(() => {
            controller?.abort();
            resolve(DEADLINE);
        }, TEMPLATE_CATEGORY_BULK_REQUEST_DEADLINE_MS);
    });
    try {
        return await work(controller?.signal, deadline);
    } finally {
        if (timer) clearTimeout(timer);
        external?.removeEventListener('abort', onExternalAbort);
    }
};

/** 자체 마감이 먼저 끊는다. ky 타이머는 그보다 늦게 둔다. */
const kyOptions = (signal: AbortSignal | undefined, extra: Record<string, unknown> = {}) => ({
    timeout: TEMPLATE_CATEGORY_BULK_REQUEST_DEADLINE_MS + 5000,
    ...(signal ? { signal } : {}),
    ...extra,
});

/** 본문까지 읽은 JSON. 마감이면 DEADLINE. */
const readBody = async (res: Response, deadline: Promise<Deadline>): Promise<unknown | Deadline> =>
    Promise.race([safeJson(res).catch(() => null), deadline]);

/** 마감 안에서 POST 하고 본문까지 읽는다. 헤더 전에 끊기면 네트워크 실패(같은 키 재시도), 헤더 뒤면 결과 미확인. */
const postJobWithDeadline = (
    url: string,
    json: Record<string, string>,
    external?: AbortSignal,
): Promise<TemplateCategoryBulkJob> =>
    runWithDeadline(async (signal, deadline) => {
        // noAuthRetry: 401 이면 공용 클라이언트가 토큰을 갱신해 같은 POST 를 다시 보내지 않게 한다 —
        // 그 사이 로그아웃·계정 전환이 있었으면 옛 세션으로 쓰기가 나간다.
        const res = await Promise.race([apiClient.post(url, kyOptions(signal, { json, noAuthRetry: true }) as any), deadline]);
        if (res === DEADLINE) throw timeoutError();
        const body = await readBody(res as Response, deadline);
        if (body === DEADLINE) throw unconfirmedError();
        const job = parseTemplateCategoryBulkJob(body);
        if (!job) throw unconfirmedError();
        return job;
    }, external);


/** @method post @brief 일괄 적용. 네트워크 재시도에는 같은 requestId·previewHash 를 쓴다. */
export const postTemplateCategoryBulk = async ({
    centerId,
    templateId,
    requestId,
    previewHash,
    signal,
}: {
    centerId: string;
    templateId: string;
    requestId: string;
    previewHash: string;
    signal?: AbortSignal;
}) => {
    return postJobWithDeadline(bulkBase(centerId, templateId), { requestId, previewHash }, signal);
};

/**
 * @method post @brief 일괄 적용 되돌리기.
 * requestId 는 확인 창 하나에 하나 — 응답을 못 받은 재시도에는 같은 키를 써야 서버가 결과를 재생한다
 * (새 키면 두 번째 REVERT job 이 생겨 보통 「0건」으로 끝난다).
 */
export const postTemplateCategoryBulkRevert = async ({
    centerId,
    templateId,
    jobId,
    requestId,
    signal,
}: {
    centerId: string;
    templateId: string;
    jobId: string;
    requestId: string;
    signal?: AbortSignal;
}) => {
    return postJobWithDeadline(`${bulkBase(centerId, templateId)}/${encodeURIComponent(jobId)}/revert`, { requestId }, signal);
};

/**
 * @method get @brief 이 상품의 최근 일괄 작업(최신 먼저, 최대 20건)
 * 구서버 404·권한 403 은 「작업 없음」으로 본다 — 모달에 아무것도 안 보이게(기존 경험).
 */
export const getTemplateCategoryBulkJobs = async ({
    centerId,
    templateId,
    signal: external,
}: {
    centerId: string;
    templateId: string;
    /** React Query 의 signal — 화면이 내려가면 요청을 끊는다. */
    signal?: AbortSignal;
}): Promise<TemplateCategoryBulkJob[]> => {
    return runWithDeadline(async (signal, deadline) => {
        let res: Response | Deadline;
        try {
            res = await Promise.race([
                // noAuthRetry: 이 기능의 어떤 요청도 토큰 갱신을 시작하지 않는다(다른 탭의 새 로그인 토큰을 덮어쓰지 않게).
                apiClient.get(
                    `${bulkBase(centerId, templateId)}/jobs`,
                    kyOptions(signal, { throwHttpErrors: false, noAuthRetry: true }) as any,
                ),
                deadline,
            ]);
        } catch (error) {
            // 401 — 갱신하지 않았다. 최근 작업 줄은 숨긴다(기존 모달 그대로).
            if ((error as { authRetrySkipped?: boolean })?.authRetrySkipped) return [];
            throw error;
        }
        if (res === DEADLINE) throw timeoutError();
        const r = res as Response;
        if (r.status === 404 || r.status === 403) return [];
        if (!r.ok) throw Object.assign(new Error('일괄 작업을 불러오지 못했어요.'), { status: r.status });
        const data = await readBody(r, deadline);
        if (data === DEADLINE) throw timeoutError();
        const jobs = (data as { jobs?: unknown } | null)?.jobs;
        return Array.isArray(jobs) ? (jobs as TemplateCategoryBulkJob[]) : [];
    }, external);
};

/* ------------------------------------------------------------------ requestId */

const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

/** 서버 형식(8~64자, `[A-Za-z0-9_-]`)의 새 요청 키. 확인 버튼을 누를 때 한 번 만든다. */
export const createTemplateCategoryBulkRequestId = (): string => {
    const cryptoApi: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
        const id = cryptoApi.randomUUID();
        if (REQUEST_ID_PATTERN.test(id)) return id;
    }
    let hex = '';
    if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        cryptoApi.getRandomValues(bytes);
        hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    } else {
        while (hex.length < 32) hex += Math.random().toString(16).slice(2);
    }
    return `tb-${Date.now().toString(36)}-${hex.slice(0, 32)}`;
};

/* ------------------------------------------------------------------ 시작 판정 */

/**
 * 상품 저장 직후 무엇을 띄울지.
 * - pendingJob 이 먼저다 — 완료 기록 직전에 실패하면 대상은 0건인데 PENDING 이 남는다.
 * - 대상 0건이면 아무것도 띄우지 않는다.
 */
export const decideTemplateCategoryBulkStart = (
    preview: TemplateCategoryBulkPreview | null,
): 'skip' | 'resume' | 'confirm' => {
    if (!preview) return 'skip';
    if (preview.pendingJob) return 'resume';
    if (preview.counts.targets.total <= 0) return 'skip';
    return 'confirm';
};

/** 미리보기 조회가 실패했을 때 조용히 넘어갈 상태 — 구서버(404)·권한 없음(403). 상품 저장만 된 기존 동작이다. */
export const isTemplateCategoryBulkUnavailable = (failure: SettlementClassificationFailure | null | undefined) => {
    const status = failure?.status ?? failure?.response?.status;
    return status === 404 || status === 403;
};

/* ------------------------------------------------------------------ 표시 */

export const templateCategoryBulkCategoryLabel = (key: string | null, categories: SettlementCategory[]): string => {
    if (!key) return '미분류';
    return categories.find((c) => c.key === key)?.name ?? '선택한 분류';
};

/** 목적격 조사. 한글로 끝나면 받침으로 고르고, 아니면(영문·숫자) 「을(를)」. */
export const objectParticleOf = (word: string): string => {
    const last = word.trim().slice(-1);
    const code = last.charCodeAt(0) - 0xac00;
    if (!last || code < 0 || code > 11171) return '을(를)';
    return code % 28 === 0 ? '를' : '을';
};

export type TemplateCategoryBulkFeeLine = { tone: 'warn' | 'calm'; text: string };

/**
 * 수업료 안내. 서버가 달을 주면 그 달부터 바뀔 수 있다고 알린다.
 * 달 값이 망가져 있으면 「그대로예요」로 바꾸지 않는다 — 서버가 영향이 있다고 했기 때문이다.
 */
export const describeTemplateCategoryBulkFee = (preview: TemplateCategoryBulkPreview): TemplateCategoryBulkFeeLine => {
    const fee = preview.feeChangeStartMonth;
    if (fee) {
        const year = Number(fee.year);
        const month = Number(fee.month);
        if (Number.isInteger(year) && year > 2000 && Number.isInteger(month) && month >= 1 && month <= 12) {
            return { tone: 'warn', text: `${year}년 ${month}월부터 수업료(지난 달 급여 포함)가 바뀔 수 있어요` };
        }
        return { tone: 'warn', text: '지난 달을 포함해 수업료가 바뀔 수 있어요' };
    }
    if (preview.exceptionRatesEffective) return { tone: 'calm', text: '예외 수업료가 입력된 달이 없어 수업료는 그대로예요' };
    return { tone: 'calm', text: '수업료는 그대로예요' };
};

const parseFeeImpact = (raw: unknown): TemplateCategoryBulkFeeImpact | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as any;
    const fee = r.feeChangeStartMonth;
    return {
        exceptionRatesEffective: r.exceptionRatesEffective === true,
        feeChangeStartMonth:
            fee && typeof fee === 'object' ? { year: String(fee.year ?? ''), month: String(fee.month ?? '') } : null,
    };
};

/**
 * 이어 하기 화면의 수업료 문구. 서버가 토큰과 같은 계산에서 만든 pendingJob.feeImpact 를 쓴다(§4-1).
 * 없으면(구서버) 미리보기의 현재 계산값으로 대신한다.
 */
export const describeTemplateCategoryBulkResumeFee = (
    pending: TemplateCategoryBulkPendingJob,
    fallback?: Pick<TemplateCategoryBulkPreview, 'exceptionRatesEffective' | 'feeChangeStartMonth'> | null,
): TemplateCategoryBulkFeeLine | null => {
    if (pending.kind !== 'APPLY') return null;
    const impact = parseFeeImpact(pending.feeImpact) ?? (fallback ? parseFeeImpact(fallback) : null);
    if (!impact) return null;
    return describeTemplateCategoryBulkFee(impact as TemplateCategoryBulkPreview);
};

export const templateCategoryBulkProtectedCount = (preview: TemplateCategoryBulkPreview) =>
    preview.counts.protectedCorrected + preview.counts.protectedInherited;

export const isTemplateCategoryBulkTooLarge = (preview: TemplateCategoryBulkPreview) =>
    preview.counts.targets.total > preview.maxTargets;

const fmt = (n: number) => n.toLocaleString('ko-KR');

/**
 * 적용·되돌리기 결과 한 줄. 끼어든 변경으로 건너뛴 수가 있으면 함께 적는다.
 * 분류 이름을 주면 적용 결과에 넣는다(「42장에 「공동구매」를 적용했어요」).
 */
export const describeTemplateCategoryBulkResult = (job: TemplateCategoryBulkJob | null, categoryName?: string): string => {
    const applied = num(job?.counts?.applied);
    const skipped = num(job?.counts?.skippedChanged);
    const swept = num(job?.sweptAdded);
    const isRevert = job?.kind === 'REVERT';
    const head = isRevert
        ? applied > 0
            ? `${fmt(applied)}장을 되돌렸어요`
            : '되돌릴 수강권이 없었어요'
        : applied > 0
          ? categoryName
              ? `${fmt(applied)}장에 「${categoryName}」${objectParticleOf(categoryName)} 적용했어요`
              : `${fmt(applied)}장에 적용했어요`
          : '바꾼 수강권이 없어요';
    const parts = [head];
    if (!isRevert && applied > 0 && swept > 0) parts.push(`적용 중 새로 생긴 ${fmt(swept)}장 포함`);
    if (skipped > 0) parts.push(`${fmt(skipped)}장은 그 사이 바뀌어 그대로 뒀어요`);
    return parts.join(' · ');
};

/**
 * 적용 중 새로 생긴 수강권을 다 반영하지 못했을 때(서버 sweepIncomplete)의 안내. 해당 없으면 null.
 * 남은 수는 서버 sweepRemaining 그대로(대략) — 화면에서 다시 세지 않는다.
 */
export const describeTemplateCategoryBulkSweepIncomplete = (job: TemplateCategoryBulkJob | null): string | null => {
    if (!job || job.kind !== 'APPLY' || job.sweepIncomplete !== true) return null;
    const remaining = num(job.sweepRemaining);
    const what = remaining > 0 ? `적용 중 새로 생긴 수강권 약 ${fmt(remaining)}장은` : '적용 중 새로 생긴 수강권 일부는';
    return `${what} 아직 반영되지 않았어요. 한 번 더 「발급된 수강권에도 적용」을 눌러 주세요.`;
};

/** 결과 알림에 「되돌리기」를 붙일 수 있는가 — 실제로 바꾼 적용 job 만. */
export const canRevertTemplateCategoryBulkJob = (job: TemplateCategoryBulkJob | null) =>
    !!job && job.kind === 'APPLY' && job.status === 'APPLIED' && num(job.counts?.applied) > 0 && !!job.jobId;

/* ------------------------------------------------------------------ 실패 분기 */

export type TemplateCategoryBulkFailureAction =
    /** 확인 창을 새 미리보기로 다시 그린다(새 hash). */
    | {
          type: 'stale';
          preview: TemplateCategoryBulkPreview;
          message: string;
          /** 이어 하기 재확인 409(§4-1)일 때만: 같은 job 을 이어 하는 키·토큰과 그 토큰이 승인하는 수업료 영향. */
          resume?: { requestId: string; previewHash: string; feeImpact: TemplateCategoryBulkFeeImpact | null } | null;
      }
    /** 끝나지 않은 작업 이어 하기로 넘어간다. */
    | { type: 'pending'; pending: TemplateCategoryBulkPendingJob; message: string }
    /** 같은 requestId 로 다시 보내면 이어서 끝난다(중간 실패·응답 유실). */
    | { type: 'retrySame'; message: string }
    /** 미리보기부터 다시(다른 쪽이 처리 중). */
    | { type: 'repreview'; message: string }
    /** 새 requestId 로 다시. */
    | { type: 'renewRequestId'; message: string }
    /** 200 인데 본문을 못 읽었다 — 같은 requestId 로 결과를 다시 받는다. 성공으로도 실패로도 단정하지 않는다. */
    | { type: 'unconfirmed'; message: string }
    /** 401 — 재전송하지 않았다. 로그인을 다시 맞춘 뒤 상품 창에서 확인한다. 키는 결과 미확인으로 보관. */
    | { type: 'authLost'; message: string }
    /** 더 진행할 수 없다. */
    | { type: 'stop'; message: string };

const detailsOf = (failure: SettlementClassificationFailure | null | undefined): any => {
    const payload: any = failure?.body ?? failure?.data ?? failure?.response?.data;
    return payload && typeof payload === 'object' ? payload.details : undefined;
};

export const classifyTemplateCategoryBulkFailure = (
    failure: SettlementClassificationFailure | null | undefined,
    phase: 'apply' | 'revert',
): TemplateCategoryBulkFailureAction => {
    if (
        (failure as { authRetrySkipped?: boolean } | null | undefined)?.authRetrySkipped ||
        (failure?.status ?? failure?.response?.status) === 401
    ) {
        return { type: 'authLost', message: '로그인이 바뀌었거나 만료됐어요. 다시 로그인한 뒤 상품 창에서 확인해 주세요.' };
    }
    if ((failure as { resultUnconfirmed?: boolean } | null | undefined)?.resultUnconfirmed) {
        return phase === 'apply'
            ? { type: 'unconfirmed', message: '적용 결과를 확인하지 못했어요. 이미 적용됐을 수 있어요.' }
            : { type: 'unconfirmed', message: '되돌리기 결과를 확인하지 못했어요. 이미 되돌렸을 수 있어요.' };
    }
    const code = getSettlementClassificationErrorCode(failure);
    const status = failure?.status ?? failure?.response?.status;
    const details = detailsOf(failure);
    switch (code) {
        case 'SETTLEMENT_BULK_PREVIEW_STALE': {
            const preview = parseTemplateCategoryBulkPreview(details?.preview);
            const r = details?.resume;
            const resume =
                r && typeof r.requestId === 'string' && typeof r.previewHash === 'string' && r.previewHash
                    ? {
                          requestId: r.requestId,
                          previewHash: r.previewHash,
                          feeImpact: parseFeeImpact(r.feeImpact) ?? (preview ? parseFeeImpact(preview) : null),
                      }
                    : null;
            return preview
                ? { type: 'stale', preview, message: '그 사이 수강권이 바뀌어 숫자를 새로 불러왔어요.', resume }
                : { type: 'repreview', message: '그 사이 수강권이 바뀌었어요. 다시 확인해 주세요.' };
        }
        case 'SETTLEMENT_BULK_PENDING_EXISTS':
            return isPendingJob(details)
                ? { type: 'pending', pending: details, message: '끝나지 않은 일괄 작업이 있어요.' }
                : { type: 'repreview', message: '끝나지 않은 일괄 작업이 있어요. 다시 확인해 주세요.' };
        case 'SETTLEMENT_BULK_IN_PROGRESS':
            return { type: 'repreview', message: getSettlementClassificationErrorMessage(failure) };
        case 'SETTLEMENT_BULK_REQUEST_ID_REUSED':
            return { type: 'renewRequestId', message: '요청이 겹쳤어요. 한 번 더 눌러 주세요.' };
        case 'SETTLEMENT_BULK_SCAN_TIMEOUT':
            // 쓰기 전에 끊겼다 — 같은 키로 다시 보내도 안전하다.
            return { type: 'retrySame', message: getSettlementClassificationErrorMessage(failure) };
        case 'SETTLEMENT_BULK_TOO_LARGE': {
            const max = typeof details?.max === 'number' ? details.max : 5000;
            return { type: 'stop', message: `${fmt(max)}장이 넘어 한 번에 바꿀 수 없어요. 라포채널로 문의해 주세요.` };
        }
        case 'SETTLEMENT_BULK_TEMPLATE_NOT_FOUND':
        case 'SETTLEMENT_BULK_JOB_NOT_FOUND':
        case 'SETTLEMENT_BULK_NOT_REVERTIBLE':
        case 'SETTLEMENT_CATEGORY_DANGLING':
            return { type: 'stop', message: getSettlementClassificationErrorMessage(failure) };
        default:
            break;
    }
    if (status === 403) return { type: 'stop', message: '정산 분류는 센터 관리자만 바꿀 수 있어요.' };
    if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        return { type: 'stop', message: getSettlementClassificationErrorMessage(failure) };
    }
    // 500·네트워크 끊김·시간 초과 — 서버가 일부를 바꿨을 수 있다. 같은 키로 다시 보내면 이어서 끝난다.
    return phase === 'apply'
        ? { type: 'retrySame', message: '적용을 끝내지 못했어요. 다시 누르면 이어서 끝나요.' }
        : { type: 'retrySame', message: '되돌리기를 끝내지 못했어요. 다시 누르면 이어서 끝나요.' };
};

/* ------------------------------------------------------------------ 상품 모달의 최근 작업 한 줄 */

export type TemplateCategoryBulkLatestLine = {
    text: string;
    /** revert = 되돌리기 확인 창, resume = 이어 하기(미리보기의 pendingJob 으로), reapply = 새 미리보기부터 다시 적용 */
    action: TemplateCategoryBulkLatestAction | null;
    /** 두 번째 동작 — 일부 미반영(sweepIncomplete) 최신 적용의 「되돌리기」(「마저 적용」과 함께, 12회차). */
    secondaryAction?: TemplateCategoryBulkLatestAction | null;
};
export type TemplateCategoryBulkLatestAction = {
    label: string;
    type: 'revert' | 'resume' | 'reapply';
    job: TemplateCategoryBulkJob;
};

/** 'M/D'. 날짜를 못 읽으면 빈 문자열(문장에서 뺀다). 표시용 — 기기 시간대 기준. */
const shortDateOf = (value: string | null | undefined): string => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}`;
};

/**
 * 가장 최근 일괄 작업 한 줄. 작업이 없으면 null(아무것도 안 보인다).
 * - 끝나지 않은 작업(PENDING) → 「적용이 끝나지 않았어요 · 이어서 끝내기」
 * - 적용 → 「10/16 42장에 「공동구매」 적용 · 되돌리기」, 되돌린 뒤엔 「· 되돌림」
 * - 최신이 되돌리기 → 그 원래 적용을 「되돌림」으로
 */
export const describeTemplateCategoryBulkLatest = (
    jobs: TemplateCategoryBulkJob[] | null | undefined,
    categories: SettlementCategory[],
): TemplateCategoryBulkLatestLine | null => {
    const latest = jobs?.[0];
    if (!latest || !latest.jobId) return null;
    if (latest.status === 'PENDING') {
        return {
            text: latest.kind === 'REVERT' ? '되돌리기가 끝나지 않았어요' : '적용이 끝나지 않았어요',
            action: { label: '이어서 끝내기', type: 'resume', job: latest },
        };
    }
    if (latest.status !== 'APPLIED') return null;
    const apply =
        latest.kind === 'APPLY' ? latest : (jobs ?? []).find((j) => j.jobId === latest.revertOfJobId && j.kind === 'APPLY');
    if (!apply) {
        // 원 적용이 최근 20건 밖으로 밀렸다(다시 정리를 반복). 되돌리기 job 이 원 적용 id 를 알고 있으니
        // 그걸로 「다시 정리」를 유지한다 — 복구 버튼이 목록 길이 때문에 사라지면 안 된다.
        const date = shortDateOf(latest.appliedAt ?? latest.createdAt);
        const text = `${date ? `${date} ` : ''}일괄 적용 되돌림`;
        if (latest.kind !== 'REVERT' || !latest.revertOfJobId) return { text, action: null };
        const recoveredApply: TemplateCategoryBulkJob = {
            jobId: latest.revertOfJobId,
            kind: 'APPLY',
            status: 'APPLIED',
            templateId: latest.templateId,
            toKey: null,
            revertOfJobId: null,
            revertedByJobId: latest.jobId,
            counts: { planned: 0, applied: 0, skippedChanged: 0 },
        };
        return { text, action: { label: '다시 정리', type: 'revert', job: recoveredApply } };
    }
    const date = shortDateOf(apply.appliedAt ?? apply.createdAt);
    const name = !apply.toKey ? '미분류' : categories.find((c) => c.key === apply.toKey)?.name;
    const applied = num(apply.counts?.applied);
    const what = name ? `「${name}」 적용` : '정산 분류 적용';
    const head = `${date ? `${date} ` : ''}${applied > 0 ? `${fmt(applied)}장에 ` : ''}${what}`;
    const reverted = latest.kind === 'REVERT' || !!apply.revertedByJobId;
    if (reverted) {
        // 계약 §5: 되돌림 완료 뒤에도 「다시 정리」 — 되돌리는 사이·직후 저장된 사본을 정리한다(다시 눌러도 안전).
        const revertedApply = { ...apply, revertedByJobId: apply.revertedByJobId ?? latest.jobId };
        return { text: `${head} · 되돌림`, action: { label: '다시 정리', type: 'revert', job: revertedApply } };
    }
    // 적용 중 새로 생긴 수강권을 다 반영하지 못한 최신 적용(서버 sweepIncomplete) — 마저 적용하는 길을 먼저 주고,
    // 실제로 바꾼 장수가 있으면 되돌리기도 함께 둔다(결과 알림의 「상품 창에서도 되돌릴 수 있어요」와 맞춘다 — 12회차).
    if (latest.kind === 'APPLY' && apply.sweepIncomplete === true) {
        return {
            text: `${head} · 일부 아직 반영 안 됨`,
            action: { label: '마저 적용', type: 'reapply', job: apply },
            secondaryAction: applied > 0 ? { label: '되돌리기', type: 'revert', job: apply } : null,
        };
    }
    if (applied <= 0) return { text: `${head} · 바뀐 수강권 없음`, action: null };
    return { text: head, action: { label: '되돌리기', type: 'revert', job: apply } };
};
